// « Paiements » — payments by tender for a period, and the card side matched
// against the Sextant terminal. Pure logic: no Supabase, no DOM (the
// logic.ts pattern), so every rule below is unit-tested.
//
// Two sources, deliberately kept apart:
//   - the Vega mirror (`MirrorPayment`, one row per GDETREG movement) — what the
//     desk keyed, the book the bank is reconciled against until go-live;
//   - the Sextant portal snapshot (`bosa fetch-sextant`) — what the terminal
//     actually charged, to the second.
//
// The rules are the accounting repo's, not new ones: money in is ARVE + REGL
// (never ARDE — the deposit being APPLIED is an internal move that produces no
// bank line), card methods are 010 / 011 / 012, and a card movement is matched
// to a terminal charge of the SAME AMOUNT within ±2 DAYS, each charge consumed
// ONCE (macompta-integration `BANK-REC-BRIEF.md` §2, measured on August 2026:
// 715 movements, 667 matched, 133 312,86 € = the 511210 net to the cent).

import type { MirrorPayment } from '@lemontaigu/pms-core';
import { monthEndKey, shiftDay } from './logic.js';

// ── Tenders ──────────────────────────────────────────────────────────────────

export interface TenderInfo {
  label: string;
  /** Settled through the card terminal — the ones that can be matched. */
  card: boolean;
  /** The Sextant contract this method settles under (card only). */
  contract?: string;
}

/** GPARADIV « modes de règlement » (memory: vega-payment-to-booking-keys).
 *  Anything not listed is shown under its raw code — never guessed
 *  (Hard Rule 1), exactly as `vega_rec_payment_v1.tender` does. */
export const TENDERS: Readonly<Record<string, TenderInfo>> = {
  '001': { label: 'Espèces', card: false },
  '002': { label: 'Chèque', card: false },
  '006': { label: 'Chèques vacances (ANCV)', card: false },
  '010': { label: 'CB Visa / Mastercard', card: true, contract: '3960653' },
  '011': { label: 'American Express', card: true, contract: '8336362470' },
  '012': { label: 'CB à distance (VAD)', card: true, contract: '3960750' },
  '070': { label: 'Remboursement espèces', card: false },
  '080': { label: 'Remboursement arrhes', card: false },
  '081': { label: 'Profit sur arrhes', card: false },
  '090': { label: 'Virement', card: false },
  '099': { label: 'Écart de règlement', card: false },
};

export const tenderInfo = (code: string): TenderInfo =>
  TENDERS[code] ?? { label: `Code ${code}`, card: false };

/** Order of the summary: the tenders the desk takes every day first, then the
 *  rest by code. */
const TENDER_ORDER = ['010', '011', '012', '001', '002', '006', '090'];
const tenderRank = (code: string): number => {
  const i = TENDER_ORDER.indexOf(code);
  return i === -1 ? TENDER_ORDER.length : i;
};

export const toCents = (n: number): number => Math.round(n * 100);

/** « 1 234,56 » — no currency sign, tabular columns carry the unit. */
export const fmtCents = (cents: number): string =>
  (cents / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ── Which movements are money in ─────────────────────────────────────────────

const MONEY_IN_KINDS: ReadonlySet<string> = new Set(['ARVE', 'REGL']);

/** A movement that put (or, signed, took back) money through a tender.
 *  `settledAmount` null means NOTHING moved on the row (a REGL paid by an
 *  already-held deposit) — a value, not a missing one — and method 000 is the
 *  same fact from the other side. */
export function isEncaissement(p: MirrorPayment): boolean {
  return (
    p.kind !== null &&
    MONEY_IN_KINDS.has(p.kind) &&
    p.settledAmount !== null &&
    p.method !== null &&
    p.method !== '000'
  );
}

export interface TenderRow {
  code: string;
  label: string;
  card: boolean;
  count: number;
  /** ARVE — deposits taken. */
  arrhesCents: number;
  /** REGL — settlements at checkout. */
  reglCents: number;
  totalCents: number;
}

export interface OtherMovement {
  kind: string;
  label: string;
  count: number;
  /** Sum of the rows that carry a value; null rows add nothing (not zero). */
  cents: number;
}

export interface TenderSummary {
  rows: TenderRow[];
  count: number;
  totalCents: number;
  /** Everything that is NOT money in: ARDE (deposit applied), PRPE / PRPD
   *  (prise en charge), RECD, CLDI — shown, never summed into the total. */
  others: OtherMovement[];
}

export function summarise(payments: readonly MirrorPayment[]): TenderSummary {
  const byCode = new Map<string, TenderRow>();
  const others = new Map<string, OtherMovement>();
  let count = 0;
  let totalCents = 0;

  for (const p of payments) {
    if (isEncaissement(p)) {
      const code = p.method as string;
      const cents = toCents(p.settledAmount as number);
      const info = tenderInfo(code);
      const row =
        byCode.get(code) ??
        { code, label: info.label, card: info.card, count: 0, arrhesCents: 0, reglCents: 0, totalCents: 0 };
      row.count += 1;
      if (p.kind === 'ARVE') row.arrhesCents += cents;
      else row.reglCents += cents;
      row.totalCents += cents;
      byCode.set(code, row);
      count += 1;
      totalCents += cents;
    } else {
      const kind = p.kind ?? '?';
      const o =
        others.get(kind) ?? { kind, label: p.kindLabel ?? kind, count: 0, cents: 0 };
      o.count += 1;
      if (p.settledAmount !== null) o.cents += toCents(p.settledAmount);
      others.set(kind, o);
    }
  }

  return {
    rows: [...byCode.values()].sort(
      (a, b) => tenderRank(a.code) - tenderRank(b.code) || a.code.localeCompare(b.code),
    ),
    count,
    totalCents,
    others: [...others.values()].sort((a, b) => a.kind.localeCompare(b.kind)),
  };
}

// ── Reading a period in bounded slices ───────────────────────────────────────

/** [from, to] cut at month ends. The mirror read refuses past 5 000 rows
 *  rather than truncating (ADR-0022), and a fiscal year is a lot of
 *  movements — one read per month keeps every call far under the ceiling. */
export function monthSlices(from: string, to: string): { from: string; to: string }[] {
  const slices: { from: string; to: string }[] = [];
  let start = from;
  while (start <= to) {
    const end = monthEndKey(start);
    slices.push({ from: start, to: end < to ? end : to });
    start = shiftDay(end, 1);
  }
  return slices;
}

// ── The Sextant portal snapshot ──────────────────────────────────────────────

export interface Tap {
  /** The portal's transaction uuid — the de-duplication key across snapshots. */
  id: string;
  /** Local time of the tap, to the second: 'YYYY-MM-DDTHH:MM:SS'. */
  at: string;
  /** SIGNED: a reversal (DEBIT_REVERSAL) is negative. */
  cents: number;
  contract: string;
  authorisation: string | null;
  /** Last four of the masked card — a handle, never a PAN. */
  last4: string;
  application: string;
  remise: string;
}

export interface Remise {
  /** « numéro de remise » — what the bank statement quotes. */
  number: string;
  settledAt: string;
  contract: string;
  label: string;
  taps: number;
  cents: number;
  /** The portal's own total for the remise — a cross-check, not a source. */
  portalCents: number;
}

export interface SnapshotRead {
  taps: Tap[];
  remises: Remise[];
  warnings: string[];
}

const RESULTS_OK = new Set(['AUTHORIZED', 'NOT_VERIFIED']);
const TYPES_OK = new Set(['DEBIT', 'DEBIT_REVERSAL']);
const UNMASKED_PAN = /^\d{13,19}$/;

type Json = Record<string, unknown>;
const str = (v: unknown): string => (typeof v === 'string' ? v : '');
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** Read one or more `sextant-portal-YYYY-MM.json` snapshots into taps.
 *
 *  Refuses (throws) on anything that is not that shape, and on an UNMASKED card
 *  number — the same guard `portal_client.assert_no_pan` applies when the file
 *  is written; a PAN must not get as far as this page's memory. Only taps that
 *  are money are kept: authorised (or offline-validated), a debit or its
 *  reversal, inside a remise that carries a number. Refusals and `TNA` are not
 *  money and are counted in the warnings, not hidden. */
export function readSnapshots(snapshots: readonly unknown[]): SnapshotRead {
  const taps = new Map<string, Tap>();
  const remises = new Map<string, Remise>();
  const warnings: string[] = [];
  let skipped = 0;

  for (const snap of snapshots) {
    const root = snap as Json | null;
    if (!root || root.source !== 'sextant-portal' || !Array.isArray(root.remises)) {
      throw new Error('Ce fichier n’est pas un export du portail Sextant (sextant-portal-AAAA-MM.json).');
    }
    for (const r of root.remises as Json[]) {
      const number = str(r.reconciliationNumber);
      const remiseTaps = Array.isArray(r.transactions) ? (r.transactions as Json[]) : [];
      if (!number) continue; // an empty envelope, no remise behind it
      const kept: Tap[] = [];
      for (const t of remiseTaps) {
        const pan = str(t.pan);
        if (UNMASKED_PAN.test(pan)) {
          throw new Error('Le fichier contient un numéro de carte non masqué — refusé, ne pas le conserver.');
        }
        const amount = num(t.amount);
        const at = str(t.localTransactionDateTime);
        if (
          amount === null ||
          !at ||
          !RESULTS_OK.has(str(t.authorizationCodeResult)) ||
          !TYPES_OK.has(str(t.type))
        ) {
          skipped += 1;
          continue;
        }
        kept.push({
          id: str(t.uuid) || `${number}/${at}/${amount}`,
          at,
          cents: toCents(amount),
          contract: str(t.contract) || str(r.contract),
          authorisation: str(t.authorizationNumber) || null,
          last4: pan.slice(-4),
          application: str(t.application),
          remise: number,
        });
      }
      for (const k of kept) taps.set(k.id, k);
      remises.set(str(r.uuid) || number, {
        number,
        settledAt: str(r.localTransactionDateTime),
        contract: str(r.contract),
        label: str(r.contractProfileLabel),
        taps: kept.length,
        cents: kept.reduce((s, k) => s + k.cents, 0),
        portalCents: toCents(num(r.totalAmount) ?? 0),
      });
    }
  }

  if (skipped) {
    warnings.push(`${skipped} opération(s) du terminal ignorée(s) : refusées, en échec ou illisibles (pas de l’argent).`);
  }
  for (const r of remises.values()) {
    if (r.cents !== r.portalCents) {
      warnings.push(
        `Remise ${r.number} : les opérations totalisent ${fmtCents(r.cents)} € mais le portail annonce ${fmtCents(r.portalCents)} €.`,
      );
    }
  }

  return {
    taps: [...taps.values()].sort((a, b) => a.at.localeCompare(b.at) || a.cents - b.cents),
    remises: [...remises.values()].sort((a, b) => a.settledAt.localeCompare(b.settledAt)),
    warnings,
  };
}

// ── Matching ─────────────────────────────────────────────────────────────────

/** The window either side of the movement's date. A card keyed late or
 *  settled across midnight lands a day or two off; wider than this and one
 *  charge can pay two guests (BANK-REC-BRIEF §2.3 — « arithmetic succeeds
 *  exactly where attribution has failed »). */
export const MATCH_WINDOW_DAYS = 2;

const dayOf = (iso: string): string => iso.slice(0, 10);
const dayDiff = (a: string, b: string): number =>
  Math.round((Date.parse(`${a}T12:00:00Z`) - Date.parse(`${b}T12:00:00Z`)) / 86_400_000);

export interface CardMatch {
  /** Index into the `movements` array passed in. */
  movement: number;
  tap: Tap;
  /** Whole days between the movement's date and the tap's. */
  dayOffset: number;
}

export interface MatchResult {
  matches: CardMatch[];
  /** Indices of movements with no terminal charge. */
  unmatchedMovements: number[];
  unmatchedTaps: Tap[];
  /** Tap dates the snapshots cover. A movement or tap whose ±2-day window is
   *  not wholly inside this is INCONCLUSIVE when unmatched — the partner may
   *  simply be in a snapshot that was not loaded. */
  coverage: { from: string; to: string } | null;
}

/** Match card movements (Vega) to terminal charges (Sextant): same contract,
 *  same signed amount, nearest day first (0, then ±1, then ±2), each charge
 *  used once. Nearest-first over passes rather than a single sweep, so an
 *  exact-day pair is never robbed by an earlier movement that could have taken
 *  a neighbour. */
export function matchCard(
  movements: readonly { date: string; cents: number; method: string }[],
  taps: readonly Tap[],
): MatchResult {
  const used = new Set<string>();
  const matched = new Map<number, CardMatch>();
  // Indexed by (contract, amount): a year of taps is ~10 000, and a linear
  // scan per movement per pass is what would make that visible.
  const pool = new Map<string, Tap[]>();
  for (const t of taps) {
    const key = `${t.contract}|${t.cents}`;
    const list = pool.get(key);
    if (list) list.push(t);
    else pool.set(key, [t]);
  }
  const order = movements.map((_, i) => i).sort((a, b) => movements[a]!.date.localeCompare(movements[b]!.date) || a - b);

  for (let distance = 0; distance <= MATCH_WINDOW_DAYS; distance += 1) {
    for (const i of order) {
      if (matched.has(i)) continue;
      const m = movements[i]!;
      const contract = TENDERS[m.method]?.contract;
      if (!contract) continue;
      const tap = pool
        .get(`${contract}|${m.cents}`)
        ?.find((t) => !used.has(t.id) && Math.abs(dayDiff(dayOf(t.at), m.date)) === distance);
      if (!tap) continue;
      used.add(tap.id);
      matched.set(i, { movement: i, tap, dayOffset: dayDiff(dayOf(tap.at), m.date) });
    }
  }

  return {
    matches: [...matched.values()].sort((a, b) => a.movement - b.movement),
    unmatchedMovements: order.filter((i) => !matched.has(i)),
    unmatchedTaps: taps.filter((t) => !used.has(t.id)),
    coverage: taps.length
      ? { from: dayOf(taps[0]!.at), to: dayOf(taps[taps.length - 1]!.at) }
      : null,
  };
}

/** True when an unmatched item's whole ±2-day window lies inside the loaded
 *  snapshots (and, for a tap, the Vega period read) — only then is « no
 *  partner » a finding rather than a missing file. */
export function isConclusive(date: string, within: { from: string; to: string } | null): boolean {
  return (
    within !== null &&
    shiftDay(date, -MATCH_WINDOW_DAYS) >= within.from &&
    shiftDay(date, MATCH_WINDOW_DAYS) <= within.to
  );
}
